import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { ChevronRightIcon, ChevronLeftIcon, GiftIcon, SparklesIcon } from '@heroicons/react/24/outline';

const DesktopShoghtaneh = () => {
  const navigate = useNavigate();
  const { resolveMediaUrl } = useSettings();
  const [shoghtanehStatus, setShoghtanehStatus] = useState(null);
  const [shoghtanehRemaining, setShoghtanehRemaining] = useState(0);
  const shoghtanehFetchLockRef = useRef(false);

  // --- Helpers from MobileHomeUI2 ---
  const toFaDigits = (value) => {
    const digits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return String(value ?? '').replace(/\d/g, (d) => digits[Number(d)] ?? d);
  };

  const formatPrice = (price) => {
    const n = Number(price);
    if (!Number.isFinite(n)) return '';
    return `${new Intl.NumberFormat('fa-IR').format(n)} تومان`;
  };

  const getOriginalPrice = (product) => {
    const n = Number(product?.price ?? product?.originalPrice ?? product?.original_price);
    return Number.isFinite(n) ? n : null;
  };

  const getDiscountedPrice = (product) => {
    const n = Number(
      product?.discount_price ??
      product?.discountPrice ??
      product?.final_price ??
      product?.finalPrice ??
      product?.price
    );
    return Number.isFinite(n) ? n : null;
  };

  const getDiscountPercent = (product) => {
    const original = getOriginalPrice(product);
    const discounted = getDiscountedPrice(product);
    if (!Number.isFinite(original) || !Number.isFinite(discounted)) return null;
    if (original <= 0 || discounted >= original) return null;
    return Math.max(1, Math.round((1 - discounted / original) * 100));
  };

  const formatPercentFa = (percent) => {
    const n = Number(percent);
    if (!Number.isFinite(n)) return '';
    return new Intl.NumberFormat('fa-IR').format(n);
  };

  const formatCountdownFa = (secondsTotal) => {
    const total = Math.max(0, Math.floor(Number(secondsTotal) || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const ascii = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return toFaDigits(ascii);
  };

  const pickProductImage = (product) => {
    const candidates = [
      product?.main_image,
      product?.mainImage,
      product?.main_image_url,
      product?.mainImageUrl,
      product?.image,
      product?.thumbnail_url,
      product?.thumbnail,
      Array.isArray(product?.images)
        ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.image)
        : null,
    ];
    for (const c of candidates) {
      const resolved = resolveMediaUrl(c);
      if (resolved) return resolved;
    }
    return null;
  };
  // --------------------------------

  const shoghtanehPhase = shoghtanehStatus?.phase || 'disabled';
  const shoghtanehRemainingSeconds = Math.max(0, Number(shoghtanehStatus?.remaining_seconds) || 0);

  // Use a ref for the scroll container
  const scrollContainerRef = useRef(null);

  const fetchShoghtaneh = useCallback(async () => {
    if (shoghtanehFetchLockRef.current) return;
    shoghtanehFetchLockRef.current = true;
    try {
      const res = await productsAPI.getShoghtanehStatus();
      const data = res?.data || null;
      setShoghtanehStatus(data);
      setShoghtanehRemaining(Math.max(0, Number(data?.remaining_seconds) || 0));
    } catch (_) {
      setShoghtanehStatus(null);
      setShoghtanehRemaining(0);
    } finally {
      shoghtanehFetchLockRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchShoghtaneh();
    const id = window.setInterval(fetchShoghtaneh, 30000);
    return () => window.clearInterval(id);
  }, [fetchShoghtaneh]);

  useEffect(() => {
    setShoghtanehRemaining(shoghtanehRemainingSeconds);
  }, [shoghtanehRemainingSeconds]);

  useEffect(() => {
    if (shoghtanehPhase === 'disabled') return;
    if (shoghtanehRemaining <= 0) {
      fetchShoghtaneh();
      return;
    }
    const id = window.setInterval(() => {
      setShoghtanehRemaining((prev) => Math.max(0, (Number(prev) || 0) - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [fetchShoghtaneh, shoghtanehPhase, shoghtanehRemaining]);

  if (shoghtanehPhase === 'disabled') return null;

  const shoghtanehProducts = Array.isArray(shoghtanehStatus?.products) ? shoghtanehStatus.products : [];
  const shoghtanehPlaceholderCount = Math.max(0, Number(shoghtanehStatus?.placeholder_count) || 0);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full bg-kadolar-600 py-8 mb-12 rounded-3xl overflow-hidden shadow-2xl relative">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col lg:flex-row gap-8 items-center">
        
        {/* Left Side: Timer & Branding (Fixed Width) */}
        <div className="w-full lg:w-1/4 flex flex-col items-center justify-center text-white text-center">
            <div className="relative mb-4">
                <img src="/ui2/giftbox1.png" alt="Shoghtaneh Gift" className="w-32 h-32 object-contain animate-bounce-slow drop-shadow-lg" />
                <SparklesIcon className="absolute top-0 right-0 w-8 h-8 text-yellow-300 animate-pulse" />
            </div>
            <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
                <GiftIcon className="w-8 h-8" />
                شگفتانه
            </h2>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 w-full max-w-[280px]">
                <div className="text-4xl font-mono font-bold tracking-widest text-yellow-300 drop-shadow-md" dir="ltr">
                    {formatCountdownFa(shoghtanehRemaining)}
                </div>
                <div className="text-xs text-white/80 mt-2">زمان باقی‌مانده تا پایان تخفیف‌ها</div>
            </div>
             {shoghtanehPhase === 'rest' && (shoghtanehStatus?.rest_message || '').trim() && (
                <div className="mt-4 bg-red-500/80 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  {shoghtanehStatus.rest_message}
                </div>
             )}
        </div>

        {/* Right Side: Products Slider (Scrollable) */}
        <div className="w-full lg:w-3/4 relative group">
            
            {/* Navigation Buttons */}
            <button 
                onClick={scrollRight}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-kadolar-700 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -ml-4"
            >
                <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button 
                onClick={scrollLeft}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-kadolar-700 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -mr-4"
            >
                <ChevronRightIcon className="w-6 h-6" />
            </button>

            <div 
                ref={scrollContainerRef}
                className="flex gap-4 overflow-x-auto pb-4 pt-2 px-2 scrollbar-hide snap-x snap-mandatory"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {(shoghtanehPhase === 'active' || shoghtanehPhase === 'reserve') && shoghtanehProducts.length > 0 ? (
                    shoghtanehProducts.map((product) => (
                        <div 
                            key={product.id} 
                            className="min-w-[220px] max-w-[220px] bg-white rounded-2xl p-4 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer snap-start flex flex-col"
                            onClick={() => navigate(`/product/${product.id}`)}
                        >
                            <div className="relative aspect-[5/6] mb-4 bg-gray-50 rounded-xl overflow-hidden group/img">
                                {pickProductImage(product) ? (
                                    <img 
                                        src={pickProductImage(product)} 
                                        alt={product.title} 
                                        className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-500" 
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                        <GiftIcon className="w-12 h-12" />
                                    </div>
                                )}
                                {getDiscountPercent(product) && (
                                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">
                                        {formatPercentFa(getDiscountPercent(product))}% تخفیف
                                    </div>
                                )}
                            </div>
                            
                            <h3 className="font-bold text-gray-800 text-sm mb-2 line-clamp-2 min-h-[40px] leading-relaxed">
                                {product.title}
                            </h3>
                            
                            <div className="mt-auto flex flex-col gap-1">
                                {getDiscountedPrice(product) !== null && getOriginalPrice(product) !== null && getDiscountedPrice(product) < getOriginalPrice(product) && (
                                    <div className="text-[#ef4444] text-xs line-through text-left pl-1">
                                        {formatPrice(getOriginalPrice(product))}
                                    </div>
                                )}
                                <div className="font-bold text-lg text-left pl-1" style={{ color: '#16a34a' }}>
                                    {getDiscountedPrice(product) !== null ? formatPrice(getDiscountedPrice(product)) : 'ناموجود'}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                     Array.from({ length: Math.max(1, shoghtanehPlaceholderCount || 4) }).map((_, idx) => (
                        <div key={`placeholder-${idx}`} className="min-w-[220px] max-w-[220px] bg-white/10 rounded-2xl p-4 border border-white/20 flex flex-col items-center justify-center text-white/50 h-[320px] snap-start">
                            <GiftIcon className="w-12 h-12 mb-4 opacity-50" />
                            <span className="text-sm font-medium">به زودی...</span>
                        </div>
                    ))
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopShoghtaneh;
